import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import { Box, Button, CircularProgress } from '@mui/material';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { useEditStockQuantity } from '../../Query/StockQuery';
import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { StockAtom } from './StockList';

interface FaqFormValues {
  id?: string;
  quantity: number;
  operation: 'add' | 'subtract' | 'replace';
}

const validationSchema = Yup.object()
  .shape({
    quantity: Yup.string()
      .matches(/^[0-9]+$/, 'Only numbers are allowed')
      .max(1000000, 'Add quantity cannot exceed 7 digits'),
    substractQuantity: Yup.string()
      .matches(/^[0-9]+$/, 'Only numbers are allowed')
      .max(1000000, 'Substract quantity cannot exceed 7 digits'),
    replaceQuantity: Yup.string()
      .matches(/^[0-9]+$/, 'Only numbers are allowed')
      .max(1000000, 'Replace quantity cannot exceed 7 digits'),
  })
  .test(
    'at-least-one-field',
    'Please fill at least one field',
    function (value) {
      const { quantity, substractQuantity, replaceQuantity } = value;
      if (!quantity && !substractQuantity && !replaceQuantity) {
        return this.createError({
          path: 'quantity',
          message: 'Please fill at least one field',
        });
      }
      return true;
    }
  );

const CustomButton = ({
  formikProps,
  onCancel,
  CreateFaqLoading,
}: {
  formikProps: any;
  onCancel: () => void;
  CreateFaqLoading: boolean;
}) => {
  const { warning } = useThemeColors();

  const handleFieldChange = (fieldName: string, value: string) => {
    if (value) {
      // Clear other fields
      if (fieldName === 'quantity') {
        formikProps.setFieldValue('substractQuantity', '');
        formikProps.setFieldValue('replaceQuantity', '');
        // Disable and blur other fields
        const substractField = document
          .querySelector('[name="substractQuantity"]')
          ?.closest('.MuiFormControl-root');
        const replaceField = document
          .querySelector('[name="replaceQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (substractField) {
          substractField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          substractField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (replaceField) {
          replaceField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          replaceField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      } else if (fieldName === 'substractQuantity') {
        formikProps.setFieldValue('quantity', '');
        formikProps.setFieldValue('replaceQuantity', '');
        // Disable and blur other fields
        const quantityField = document
          .querySelector('[name="quantity"]')
          ?.closest('.MuiFormControl-root');
        const replaceField = document
          .querySelector('[name="replaceQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (quantityField) {
          quantityField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          quantityField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (replaceField) {
          replaceField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          replaceField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      } else if (fieldName === 'replaceQuantity') {
        formikProps.setFieldValue('quantity', '');
        formikProps.setFieldValue('substractQuantity', '');
        // Disable and blur other fields
        const quantityField = document
          .querySelector('[name="quantity"]')
          ?.closest('.MuiFormControl-root');
        const substractField = document
          .querySelector('[name="substractQuantity"]')
          ?.closest('.MuiFormControl-root');
        if (quantityField) {
          quantityField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          quantityField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
        if (substractField) {
          substractField
            .querySelector('.MuiInputBase-root')
            ?.setAttribute(
              'style',
              'opacity: 0.7; filter: blur(0.5px); pointer-events: none'
            );
          substractField
            .querySelector('.MuiFormLabel-root')
            ?.setAttribute('style', 'opacity: 0.7; filter: blur(0.5px)');
        }
      }
    } else {
      // Reset all fields when value is cleared
      const fields = document.querySelectorAll('.MuiFormControl-root');
      fields.forEach((field) => {
        field
          .querySelector('.MuiInputBase-root')
          ?.setAttribute(
            'style',
            'opacity: 1; filter: none; pointer-events: auto'
          );
        field
          .querySelector('.MuiFormLabel-root')
          ?.setAttribute('style', 'opacity: 1; filter: none');
      });
    }
    formikProps.setFieldValue(fieldName, value);
  };

  useEffect(() => {
    const { values } = formikProps;
    if (values.quantity) {
      handleFieldChange('quantity', values.quantity);
    } else if (values.substractQuantity) {
      handleFieldChange('substractQuantity', values.substractQuantity);
    } else if (values.replaceQuantity) {
      handleFieldChange('replaceQuantity', values.replaceQuantity);
    } else {
      handleFieldChange('', '');
    }
  }, [formikProps.values]);

  return (
    <Box sx={{ display: 'flex', gap: 2, px: 2 }}>
      <Button
        type="submit"
        variant="contained"
        disabled={
          !formikProps.values.quantity &&
          !formikProps.values.substractQuantity &&
          !formikProps.values.replaceQuantity
        }
        sx={{
          textTransform: 'none',
          fontSize: 14,
          px: 3,
          py: 1,
          backgroundColor: 'rgb(76, 10, 39)',
          '&:hover': {
            backgroundColor: 'rgb(76, 10, 39, 0.8)',
          },
          '&:disabled': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(0, 0, 0, 0.26)',
          },
        }}
        onClick={(e) => {
          e.preventDefault();
          formikProps.handleSubmit();
        }}
      >
        {CreateFaqLoading ? (
          <>
            <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
            Saving...
          </>
        ) : (
          'Save'
        )}
      </Button>
      <Button
        type="button"
        variant="outlined"
        color="error"
        onClick={onCancel}
        sx={{
          bgcolor: warning.main,
          color: '#fff',
          '&:hover': {
            bgcolor: warning.dark,
            color: '#fff',
          },
        }}
      >
        Cancel
      </Button>
    </Box>
  );
};

const AddQuantity = ({ onCancel }: { onCancel: () => void }) => {
  const { mutate: editQuantity, isPending: CreateFaqLoading } =
    useEditStockQuantity();
  const stockId = useAtomValue(StockAtom);
  return (
    <>
      {AllForm({
        initialValues: {
          quantity: '',
          substractQuantity: '',
          replaceQuantity: '',
        },
        validationSchema,
        onSubmit: async (values) => {
          let operation: 'add' | 'subtract' | 'replace';
          let quantity: number;

          if (values.quantity) {
            operation = 'add';
            quantity = parseInt(values.quantity);
          } else if (values.substractQuantity) {
            operation = 'subtract';
            quantity = parseInt(values.substractQuantity);
          } else {
            operation = 'replace';
            quantity = parseInt(values.replaceQuantity);
          }

          const modifiedValues: FaqFormValues = {
            quantity,
            operation,
          };

          try {
            editQuantity(
              {
                id: stockId,
                data: modifiedValues,
              },
              {
                onSuccess: () => {
                  onCancel();
                },
                onError: (error: any) => {
                  console.log(error);
                },
              }
            );
          } catch (error) {
            console.log(error);
          }
        },
        fields: [
          {
            name: 'quantity',
            label: 'Add Quantity:',
            type: 'number',
          },
          {
            name: 'substractQuantity',
            label: 'Subtract Quantity:',
            type: 'number',
          },
          {
            name: 'replaceQuantity',
            label: 'Replace Quantity:',
            type: 'number',
          },
        ],
        customButton: (formikProps: any) => (
          <CustomButton
            formikProps={formikProps}
            onCancel={onCancel}
            CreateFaqLoading={CreateFaqLoading}
          />
        ),
      })()}
    </>
  );
};

export default AddQuantity;
